import { Button } from '@consta/uikit/Button'
import { Modal } from '@consta/uikit/Modal'
import { Table } from '@consta/uikit/Table'
import { Text } from '@consta/uikit/Text'
import { FC, useState, useEffect } from 'react'
import { IconClose } from '@consta/icons/IconClose'
import { 
    useLazyGetWaterMeasurementQuery,
    usePostWaterMeasurementMutation 
} from '../../../services/miniportService';
import { useLazyGetProjectUuidQuery } from '../../../services/projectService'

type MonitoringStage4Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    handleCloseModal: () => void;
    projectUuid: string;
}

type WaterMeasurementRow = {
    id: string;
    well_category: string;
    layer: string;
    sample_taken_count: number;
    sample_not_taken_count: number;
    water_sample_count: number;
    oil_sample_count: number;
};

const MonitoringStage4: FC<MonitoringStage4Props> = ({ 
    isOpen, 
    setIsOpen, 
    handleCloseModal,
    projectUuid 
}) => {
    const [tableData, setTableData] = useState<WaterMeasurementRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [getProject, {data: project}] = useLazyGetProjectUuidQuery()
        const dateStart= new Date();
    
        const yearStart = dateStart.getFullYear();
        const monthStart = (dateStart.getMonth() + 1).toString().padStart(2, '0');
        const dayStart = dateStart.getDate().toString().padStart(2, '0');
    
        const formattedDateStart = `${yearStart}-${monthStart}-${dayStart}`;
    
        useEffect(() => {
            getProject({uuid: projectUuid})
        },[projectUuid])

    // API хуки
    const [getWaterMeasurement] = useLazyGetWaterMeasurementQuery();
    const [postWaterMeasurement] = usePostWaterMeasurementMutation();

    // Загрузка данных при открытии модального окна
    useEffect(() => {
        if (isOpen && projectUuid) {
            loadWaterMeasurementData();
        }
    }, [isOpen, projectUuid]);

    // Функция загрузки данных
    const loadWaterMeasurementData = async () => {
        if (!projectUuid) return;

        setIsLoading(true);
        try {
            const response = await getWaterMeasurement({ project_uuid: projectUuid }).unwrap();
            transformWaterMeasurementData(response);
        } catch (error) {
            console.error('❌ Ошибка при загрузке данных измерений воды:', error);
            // Используем демо-данные если API не отвечает
            setTableData(getDemoData());
        } finally {
            setIsLoading(false);
        }
    };

    // Преобразование данных из API согласно примеру ответа
    const transformWaterMeasurementData = (data: any) => {
        //('📊 Полученные данные измерений воды:', data);
        
        if (Array.isArray(data?.wells_data)) {
            const transformedData = data?.wells_data?.map((item: any, index: number) => ({
                id: `row-${index}`,
                well_category: item.well_category || 'Не указано',
                layer: item.layer || 'Не указано',
                sample_taken_count: item.sample_taken_count || 0,
                sample_not_taken_count: item.sample_not_taken_count || 0,
                water_sample_count: item.water_sample_count || 0,
                oil_sample_count: item.oil_sample_count || 0,
            }));
            setTableData(transformedData);
        } else {
            console.warn('⚠️ Данные не в ожидаемом формате массива:', data);
            setTableData(getDemoData());
        }
    };

    // Демо-данные на случай если API не работает
    const getDemoData = (): WaterMeasurementRow[] => [
        {
            id: '1',
            well_category: 'Исследуемая скважина',
            layer: 'Layer 1',
            sample_taken_count: 1,
            sample_not_taken_count: 0,
            water_sample_count: 1,
            oil_sample_count: 1,
        },
        {
            id: '2',
            well_category: 'Исследуемая скважина',
            layer: 'Layer 2',
            sample_taken_count: 1,
            sample_not_taken_count: 0,
            water_sample_count: 1,
            oil_sample_count: 1,
        },
    ];

    // Функция для скачивания файла
    const downloadFile = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.URL.revokeObjectURL(url);
    };

    // Обработчик экспорта в Excel
    const handleExportToExcel = async () => {
        if (!projectUuid) {
            console.error('❌ projectUuid не указан');
            return;
        }

        //('📤 Начало экспорта в Excel для проекта:', projectUuid);
        setIsExporting(true);

        try {
            const blob = await postWaterMeasurement({ project_uuid: projectUuid }).unwrap();
            
            //('✅ Получен blob для скачивания:', blob);
            
            if (blob instanceof Blob) {
                const filename = `Отчет_по_измерениям_воды_${formattedDateStart}_${project?.name}.xlsx`;
                downloadFile(blob, filename);
                //('✅ Файл Excel успешно скачан:', filename);
            } else {
                console.error('❌ Полученные данные не являются Blob:', blob);
            }

        } catch (error: any) {
            console.error('❌ Ошибка при экспорте в Excel:', error);
            
            if (error?.data) {
                //('📊 Данные ошибки:', error.data);
                if (error.data instanceof Blob) {
                    //('⚠️ Ошибка содержит blob, пытаемся скачать...');
                    const filename = `water_measurement_${projectUuid}_error.xlsx`;
                    downloadFile(error.data, filename);
                }
            }
        } finally {
            setIsExporting(false);
        }
    };

    // Колонки таблицы - обновлены согласно новым полям
    const columnsMonitoring4 = [
        {
            title: 'Категория скважин',
            accessor: 'well_category',
            renderCell: (row: WaterMeasurementRow) => (
                <Text>{row.well_category}</Text>
            )
        },
        {
            title: 'Пласт',
            accessor: 'layer',
            renderCell: (row: WaterMeasurementRow) => (
                <Text>{row.layer}</Text>
            )
        },
        {
            title: 'Количество отобрано',
            accessor: 'sample_taken_count',
            renderCell: (row: WaterMeasurementRow) => (
                <Text>{row.sample_taken_count}</Text>
            )
        },
        {
            title: 'Количество не отобрано',
            accessor: 'sample_not_taken_count',
            renderCell: (row: WaterMeasurementRow) => (
                <Text>{row.sample_not_taken_count}</Text>
            )
        },
        {
            title: 'Количество образцов воды',
            accessor: 'water_sample_count',
            renderCell: (row: WaterMeasurementRow) => (
                <Text>{row.water_sample_count}</Text>
            )
        },
        {
            title: 'Количество образцов нефти',
            accessor: 'oil_sample_count',
            renderCell: (row: WaterMeasurementRow) => (
                <Text>{row.oil_sample_count}</Text>
            )
        },
    ];

    const handleClose = () => {
        setTableData([]);
        handleCloseModal();
    };

    return (
        <Modal
            isOpen={isOpen}
            hasOverlay
            onClickOutside={handleClose}
            onEsc={handleClose}
            style={{
                width: '900px',
                maxWidth: '95vw'
            }}
        >
            <div style={{
                padding: '20px', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '20px'
            }}>
                <div style={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                }}>
                    <Text size='xl' weight='bold'>
                        Мини-отчёт 2 - Сверка и измерения воды
                    </Text>
                    <Button 
                        size='s' 
                        iconLeft={IconClose} 
                        view='clear' 
                        onClick={handleClose}
                    />
                </div>
                
                {isLoading ? (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        padding: '40px' 
                    }}>
                        <Text>Загрузка данных...</Text>
                    </div>
                ) : tableData.length > 0 ? (
                    <div style={{ height: '65vh' }}>
                    <div style={{ overflowX: 'auto', height: '55vh' }}>
                        <Table
                            rows={tableData}
                            //@ts-ignore
                            columns={columnsMonitoring4} 
                            borderBetweenRows 
                            stickyHeader
                        />
                        
                    </div>
                    <Text size="s" view="secondary" style={{ marginTop: '10px' }}>
                            Всего записей: {tableData.length}
                        </Text>
                        </div>
                ) : (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        padding: '40px' 
                    }}>
                        <Text view="secondary">Нет данных для отображения</Text>
                    </div>
                )}

                <div style={{
                    display: 'flex', 
                    justifyContent: 'end', 
                    gap: '20px'
                }}>
                    <Button
                        size="m"
                        view="ghost"
                        label="Закрыть"
                        width="default"
                        onClick={handleClose}
                        disabled={isExporting}
                    />
                    <Button
                        size="m"
                        view="primary"
                        label={isExporting ? "Формирование Excel..." : "Экспорт в Excel"}
                        width="default"
                        onClick={handleExportToExcel}
                        loading={isExporting}
                        disabled={!projectUuid || tableData.length === 0 || isExporting}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default MonitoringStage4;